/**
 * Process Instances API services
 */

import apiClient from './client';
import type { ProcessInstance, ProcessStatus, ProcessTemplate, User } from '@/types';

export interface InstanceCreate {
  templateId?: string;
  title?: string;
  description?: string | null;
  categoryId?: string | null;
  tagIds?: string[];
  dueDate: string;
  assignedUserId?: string | null;
  assignedUserIds?: string[];
  status?: ProcessStatus;
}

export interface InstanceUpdate {
  dueDate?: string;
  status?: ProcessStatus;
  assignedUserId?: string;
  assignedUserIds?: string[];
  tagIds?: string[];
}

export interface InstanceFilters {
  status?: ProcessStatus[];
  categoryId?: string;
  templateId?: string;
  assignedUserId?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  includeCompleted?: boolean;
}

export interface KanbanColumn {
  status: ProcessStatus;
  title: string;
  instances: ProcessInstance[];
  count: number;
}

export interface KanbanBoard {
  columns: KanbanColumn[];
  total: number;
}

export interface CalendarDay {
  date: string;
  instances: ProcessInstance[];
}

export interface CalendarMonth {
  year: number;
  month: number;
  days: CalendarDay[];
}

// Transform instance from Cloudflare Worker backend format
const transformWorkerInstance = (data: any): ProcessInstance => {
  // Map worker state to frontend status
  const stateToStatus: Record<string, ProcessStatus> = {
    pending: 'pending',
    in_progress: 'in_progress',
    completed: 'completed',
    cancelled: 'cancelled',
  };

  const baseTemplate: ProcessTemplate = {
    id: data.template?.id || data.template_id || '',
    tenantId: data.tenant_id,
    // Worker returns project at instance level (Odoo-style), use as categoryId
    categoryId: data.template?.category_id || data.project?.id || null,
    domainId: data.template?.domain_id || null,
    title: data.template?.name || data.name || 'Untitled',
    description: data.description || null,
    recurrenceRule: { type: 'interval', every: 1, unit: 'month', startDate: data.due_date } as any,
    defaultAssigneeId: null,
    outlineDocId: null,
    outlineDocUrl: null,
    notificationDaysBefore: [],
    isActive: true,
    lastInstanceDate: null,
    createdById: null,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    // Include category object if available
    category: data.project ? {
      id: data.project.id,
      name: data.project.name,
      color: data.project.color,
    } as any : undefined,
  };

  const baseUser: User | undefined = data.assignee ? {
    id: data.assignee.id,
    email: '',
    fullName: data.assignee.name,
    isSuperAdmin: false,
    isActive: true,
    emailVerifiedAt: null,
    lastLoginAt: null,
  } : undefined;

  return {
    id: data.id,
    tenantId: data.tenant_id,
    templateId: data.template?.id || data.template_id,
    dueDate: data.due_date,
    originalDueDate: data.original_due_date || data.due_date,
    status: stateToStatus[data.state] || data.state || data.status || 'pending',
    assignedUserId: data.assignee?.id || data.assignee_id,
    completionNotes: data.completion_notes || null,
    completedAt: data.completed_at || null,
    completedById: data.completed_by || null,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    template: baseTemplate,
    assignedUser: baseUser,
    assignedUsers: (data.assignees || []).map((a: any) => ({
      id: a.id || a.user_id,
      email: a.email || '',
      fullName: a.name || a.full_name || '',
      isSuperAdmin: false,
      isActive: true,
      emailVerifiedAt: null,
      lastLoginAt: null,
    })),
    completedBy: undefined,
    // Additional worker fields stored as extra properties
    priority: data.priority,
    progress: data.progress,
    tags: data.tags || [],
  };
};

export const instancesApi = {
  list: async (filters?: InstanceFilters): Promise<ProcessInstance[]> => {
    const params: any = {};
    if (filters?.status) params.status = filters.status;
    if (filters?.categoryId) params.category_id = filters.categoryId;
    if (filters?.templateId) params.template_id = filters.templateId;
    if (filters?.assignedUserId) params.assigned_user_id = filters.assignedUserId;
    if (filters?.dueDateFrom) params.due_date_from = filters.dueDateFrom;
    if (filters?.dueDateTo) params.due_date_to = filters.dueDateTo;
    if (filters?.includeCompleted !== undefined) params.include_completed = filters.includeCompleted;

    const response = await apiClient.get('/v1/instances', { params });
    // Worker returns paginated result { items, total, limit, offset }
    const items = response.data.items || response.data || [];
    return items.map(transformWorkerInstance);
  },

  getKanban: async (params?: { categoryId?: string; assignedUserId?: string }): Promise<KanbanBoard> => {
    const queryParams: any = {};
    if (params?.categoryId) queryParams.project_id = params.categoryId;
    if (params?.assignedUserId) queryParams.assignee_id = params.assignedUserId;

    const response = await apiClient.get('/v1/instances/kanban', { params: queryParams });
    const columns = response.data.columns || [];
    return {
      columns: columns.map((col: any) => ({
        status: col.state as ProcessStatus,
        title: col.label || col.title,
        instances: (col.items || col.instances || []).map(transformWorkerInstance),
        count: (col.items || col.instances || []).length,
      })),
      total: columns.reduce((sum: number, col: any) => sum + (col.items || col.instances || []).length, 0),
    };
  },

  getCalendar: async (year: number, month: number, params?: { categoryId?: string; assignedUserId?: string }): Promise<CalendarMonth> => {
    const queryParams: any = {};
    if (params?.categoryId) queryParams.project_id = params.categoryId;
    if (params?.assignedUserId) queryParams.assignee_id = params.assignedUserId;

    const response = await apiClient.get(`/v1/instances/calendar/${year}/${month}`, { params: queryParams });

    // Worker returns flat items list, group by date for calendar
    const items = (response.data.items || []).map(transformWorkerInstance);
    const dayMap = new Map<string, ProcessInstance[]>();

    for (const instance of items) {
      const date = instance.dueDate;
      if (!dayMap.has(date)) {
        dayMap.set(date, []);
      }
      dayMap.get(date)!.push(instance);
    }

    const days: CalendarDay[] = Array.from(dayMap.entries()).map(([date, instances]) => ({
      date,
      instances,
    }));

    return {
      year: response.data.year || year,
      month: response.data.month || month,
      days,
    };
  },

  get: async (id: string): Promise<ProcessInstance> => {
    const response = await apiClient.get(`/v1/instances/${id}`);
    return transformWorkerInstance(response.data);
  },

  create: async (data: InstanceCreate): Promise<ProcessInstance> => {
    const payload: Record<string, unknown> = {
      due_date: data.dueDate,
    };
    // Support both template-based and standalone instance creation
    if (data.templateId) payload.template_id = data.templateId;
    if (data.title) payload.name = data.title;
    if (data.description !== undefined) payload.description = data.description;
    if (data.assignedUserId) payload.assignee_id = data.assignedUserId;
    if (data.assignedUserIds && data.assignedUserIds.length > 0) payload.assignee_ids = data.assignedUserIds;
    if (data.status) payload.state = data.status;
    if (data.tagIds && data.tagIds.length > 0) payload.tag_ids = data.tagIds;
    // Note: categoryId maps to project_id in the worker (Odoo compatibility)
    if (data.categoryId) payload.project_id = data.categoryId;

    const response = await apiClient.post('/v1/instances', payload);
    return transformWorkerInstance(response.data);
  },

  update: async (id: string, data: InstanceUpdate): Promise<ProcessInstance> => {
    const payload: any = {};
    if (data.dueDate !== undefined) payload.due_date = data.dueDate;
    if (data.status !== undefined) payload.state = data.status;
    if (data.assignedUserId !== undefined) payload.assignee_id = data.assignedUserId;
    if (data.assignedUserIds !== undefined) payload.assignee_ids = data.assignedUserIds;
    if (data.tagIds !== undefined) payload.tag_ids = data.tagIds;

    const response = await apiClient.patch(`/v1/instances/${id}`, payload);
    return transformWorkerInstance(response.data);
  },

  start: async (id: string): Promise<ProcessInstance> => {
    const response = await apiClient.post(`/v1/instances/${id}/start`);
    return transformWorkerInstance(response.data);
  },

  complete: async (id: string, completionNotes?: string): Promise<ProcessInstance> => {
    const response = await apiClient.post(`/v1/instances/${id}/complete`, {
      completion_notes: completionNotes,
    });
    return transformWorkerInstance(response.data);
  },

  postpone: async (id: string, newDueDate: string, reason?: string): Promise<ProcessInstance> => {
    const response = await apiClient.post(`/v1/instances/${id}/postpone`, {
      new_due_date: newDueDate,
      reason,
    });
    return transformWorkerInstance(response.data);
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/v1/instances/${id}`);
  },

  getOverdueCount: async (): Promise<number> => {
    const response = await apiClient.get('/v1/instances/overdue/count');
    return response.data.overdue_count;
  },

  getMyTasks: async (includeCompleted?: boolean): Promise<ProcessInstance[]> => {
    const params = includeCompleted !== undefined ? { include_completed: includeCompleted } : {};
    const response = await apiClient.get('/v1/instances/my-tasks', { params });
    return response.data.map(transformWorkerInstance);
  },
};
